/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Box,
} from "@mui/material";
import {
  formatBoolean,
  formatDate,
} from "../common/util/formatter";
import ReportFilter from "./components/ReportFilter";
import { useAttributePreference } from "../common/util/preferences";
import { useTranslation } from "../common/components/LocalizationProvider";
import PageLayout from "../common/components/PageLayout";
import ReportsMenu from "./components/ReportsMenu";
import usePersistedState from "../common/util/usePersistedState";
import ColumnSelect from "./components/ColumnSelect";
import { useCatch } from "../reactHelper";
import useReportStyles from "./common/useReportStyles";
import TableShimmer from "../common/components/TableShimmer";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { nativePostMessage } from "../common/components/NativeInterface";
import { sessionActions } from "../store";
import PrintHeader from "./common/PrintHeader";
import { utils, writeFileXLSX } from "xlsx";
import fetchOrThrow from "../common/util/fetchOrThrow";
import SelectField from "../common/components/SelectField";
import "./common/printStyles.css";

// A stable empty object used as a fallback
const EMPTY_MAP = {}

const columnsArray = [
  ["name", "sharedName"],
  ["uniqueId", "deviceIdentifier"],
  ["groupId", "groupDialog"],
  ["phone", "sharedPhone"],
  ["model", "deviceModel"],
  ["contact", "deviceContact"],
  ["category", "deviceCategory"],
  ["status", "deviceStatus"],
//   ["icc", "sharedIMEIChip"],
//   ["vendor", "sharedVendorChip"],
//   ["operator", "sharedOperatorChip"],
//   ["tech", "sharedTech"],
  ["lastUpdate", "deviceLastUpdate"],
  ["disabled", "sharedDisabled"],
];

const columnsMap = new Map(columnsArray);

const DevicesReportPage = () => {
  const classes = useReportStyles();
  const t = useTranslation();
  const dispatch = useDispatch();

  // Use stable empty object fallback and shallow equal to prevent rerenders
  const users = useSelector((state) => state.users.items ?? EMPTY_MAP, shallowEqual);
  const groups = useSelector((state) => state.groups.items ?? EMPTY_MAP, shallowEqual);
  const printing = useSelector((state) => state.session.printing ?? false);

  const formatDateTimeAttribute = useAttributePreference(
    "formatDateTime",
    null
  );

  const [columns, setColumns] = usePersistedState("deviceColumns", [
    "name",
    "contact",
    "uniqueId",
    "category",
    "model",
    "lastUpdate",
    "disabled",
  ]);
  const [items, setItems] = useState([]);
  const [hasResults, setHasResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const userOptions = Object.values(users)
    .filter(Boolean)
    .sort((a, b) => {
      const nameA = a?.name || a?.email || `${a?.id ?? ""}`;
      const nameB = b?.name || b?.email || `${b?.id ?? ""}`;
      return nameA.localeCompare(nameB);
    });

  // Adicionar evento de afterprint para limpar o estado de impressão
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t("deviceTitle")}.pdf`)),
      );
    }
  }, [printing, t]);

  const handleUserFilterChange = (event) => {
    const rawValue = event.target.value ?? [];
    const normalized = (Array.isArray(rawValue) ? rawValue : [rawValue])
      .filter((value) => value !== undefined && value !== null && value !== "")
      .map((value) => Number(value));
    setSelectedUserIds(normalized);
    setHasResults(false);
  };

  const fetchData = async ({ userIds = [], from, to, mail, headers } = {}) => {
    const resolvedUserIds = userIds.length ? userIds : selectedUserIds;
    setSelectedUserIds(resolvedUserIds);
    setFromDate(from || "");
    setToDate(to || "");
    setItems([]);
    setHasResults(false);
    if (!resolvedUserIds.length) {
      return [];
    }
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (from) query.append("from", from);
      if (to) query.append("to", to);
      if (mail) query.append("mail", mail);
      resolvedUserIds.forEach((id) => query.append("userId", id));

      const response = await fetchOrThrow(`/api/reports/devices?${query.toString()}`, {
        headers,
      });

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        setItems(data || []);
        setHasResults((data || []).length > 0);
        return data;
      }

      window.location.assign(window.URL.createObjectURL(await response.blob()));
      setHasResults(true);
      return [];
    } catch (error) {
      console.error("Error fetching devices:", error);
      setItems([]);
      setHasResults(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleShow = useCatch(async ({ from, to }) => {
    if (!selectedUserIds.length) {
      setItems([]);
      setHasResults(false);
      return;
    }
    await fetchData({ userIds: selectedUserIds, from, to });
    setPage(0);
  });

  const handleExportReport = useCatch(async ({ from, to } = {}) => {
    if (!selectedUserIds.length) {
      return;
    }
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;
    if (!items.length) {
      await fetchData({ userIds: selectedUserIds, from: effectiveFrom, to: effectiveTo });
    }
    const table = document.querySelector("table");
    if (!table) {
      return;
    }
    requestAnimationFrame(() =>
      writeFileXLSX(utils.table_to_book(table), `${t("deviceTitle")}.xlsx`),
    );
  });

  const handlePrintReport = useCatch(async ({ from, to } = {}) => {
    if (!selectedUserIds.length) {
      return;
    }
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;
    if (!items.length) {
      await fetchData({ userIds: selectedUserIds, from: effectiveFrom, to: effectiveTo });
    } else {
      setFromDate(effectiveFrom || "");
      setToDate(effectiveTo || "");
    }
    dispatch(sessionActions.setPrinting(true));
  });

  const formatValue = (item, key) => {
    if (!item) return "";

    try {
      switch (key) {
        case "category":
          if (!item[key]) return "";
          return t(`category${item[key].replace(/^\w/, (c) => c.toUpperCase())}`);
        case "disabled":
          return formatBoolean(item[key], t);
        case "lastUpdate":
          return item[key] ? formatDate(item[key]) : null;
        case "groupId":
          return item[key] && groups[item[key]] ? groups[item[key]].name : "";
        default:
          return item[key] || "";
      }
    } catch (error) {
      console.error(`Error formatting value for key ${key}:`, error);
      return "";
    }
  };

  // Layout para modo de impressão
  if (printing) {
    const userNames = selectedUserIds.map(id => users[id]?.name || id).filter(Boolean);

    return (
      <div>
        <PrintHeader
          title={t("deviceTitle")}
          user={userNames.join(", ")}
          from={fromDate}
          to={toDate}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length > 0 ? (
                items.map((item) => (
                  <TableRow key={`${item.id || 'id'}_${item.uniqueId || ''}`}>
                    {columns.map((key) => (
                      <TableCell key={key}>{formatValue(item, key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow style={{ height: 72 }}>
                  <TableCell align="center" colSpan={columns.length}>
                    {t("sharedNoData") || "No data available"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // Conteúdo da página
  const pageContent = (
    <div className={classes.container}>
      <div className={classes.containerMain}>
        <div className={classes.header}>
          {/* standardized export/print/agenda menu */}
          <ReportFilter
            onShow={handleShow}
            onExport={handleExportReport}
            onPrint={handlePrintReport}
            loading={loading}
            hasResults={hasResults}
            canRun={selectedUserIds.length > 0}
          >
            <Box className={classes.filterItem} sx={{ minWidth: 240 }}>
              <SelectField
                label={t("settingsUsers")}
                data={userOptions}
                value={selectedUserIds}
                onChange={handleUserFilterChange}
                multiple
                fullWidth
                titleGetter={(item) => item.name || item.email || `${item.id}`}
              />
            </Box>
            <Box className={classes.filterItem} sx={{ minWidth: 200 }}>
              <ColumnSelect
                columns={columns}
                setColumns={setColumns}
                columnsArray={columnsArray}
                rawValues={true}
                disabled={loading}
              />
            </Box>
          </ReportFilter>
        </div>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((key) => (
              <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? (
            items.length > 0 ? (
              items.map((item) => (
                <TableRow key={`${item.id || 'id'}_${item.uniqueId || ''}`}>
                  {columns.map((key) => (
                    <TableCell key={key}>{formatValue(item, key)}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow style={{ height: 72 * 5 }}>
                <TableCell align="center" colSpan={columns.length}>
                  {t("sharedNoData") || "No data available"}
                </TableCell>
              </TableRow>
            )
          ) : (
            <TableShimmer 
              columns={columns.length} 
              startAction={false}
              endAction={false}
            />
          )}
        </TableBody>
      </Table>
      </div>
    </div>
  );

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={["reportTitle", "deviceTitle"]}
    >
      {pageContent}
    </PageLayout>
  );
};

export default DevicesReportPage;
